import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Folders, Users, Clock, CheckCircle, TrendUp, CurrencyEur, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const euro = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then(r => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#4B5563]">Lädt …</div>;
  if (!stats) return <div className="text-[#4B5563]">Fehler beim Laden</div>;

  const cards = [
    { label: "Offene Fälle", value: stats.offen, icon: Folders, testid: "stat-offen" },
    { label: "In Bearbeitung", value: stats.in_bearbeitung, icon: TrendUp, testid: "stat-in-bearbeitung" },
    { label: "Abgeschlossen", value: stats.abgeschlossen, icon: CheckCircle, testid: "stat-abgeschlossen" },
    { label: "Mandanten", value: stats.total_mandanten, icon: Users, testid: "stat-mandanten" },
    { label: "Offene Fristen", value: stats.offene_fristen, icon: Clock, testid: "stat-fristen" },
    { label: "Erfolgsquote", value: `${stats.erfolgsquote} %`, icon: TrendUp, testid: "stat-erfolgsquote" },
  ];

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header>
        <p className="overline text-[#D32F2F] mb-2">Dashboard</p>
        <h1 className="font-serif text-4xl tracking-tight">Ihre Kanzlei im Überblick</h1>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map(({ label, value, icon: Icon, testid }) => (
          <div key={label} className="bg-white border border-[#E5E7EB] rounded-lg p-5 transition-all hover:-translate-y-0.5 hover:shadow-sm" data-testid={testid}>
            <Icon size={20} weight="duotone" className="text-[#1A2238]" />
            <p className="overline text-[#9CA3AF] mt-4 mb-1">{label}</p>
            <p className="font-serif text-3xl tracking-tight text-[#0A192F]">{value}</p>
          </div>
        ))}
      </div>

      {/* Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-[#1A2238] text-white rounded-lg p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#D32F2F]/20 blur-3xl" />
          <CurrencyEur size={28} weight="duotone" />
          <p className="overline text-white/60 mt-6 mb-2">Einnahmen (abgerechnet)</p>
          <p className="font-serif text-5xl tracking-tight" data-testid="einnahmen-value">{euro(stats.einnahmen)}</p>
          <p className="text-xs text-white/60 mt-4">Summe der RVG-Gebühren aus abgeschlossenen Akten</p>
        </div>

        {/* Recent Akten */}
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-2xl">Letzte Akten</h3>
            <Link to="/app/akten" className="text-sm text-[#1A2238] underline decoration-[#D32F2F]" data-testid="all-akten-link">Alle anzeigen</Link>
          </div>
          {stats.recent_akten.length === 0 ? (
            <p className="text-[#4B5563] text-sm">Noch keine Akten angelegt.</p>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {stats.recent_akten.map(a => (
                <li key={a.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Link to={`/app/akten/${a.id}`} className="font-medium hover:text-[#D32F2F] transition-colors" data-testid={`recent-akte-${a.aktenzeichen}`}>
                      {a.aktenzeichen}
                    </Link>
                    <p className="text-sm text-[#4B5563] truncate">{a.mandant_name || "–"}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Upcoming Fristen */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">Anstehende Fristen</h3>
          <Link to="/app/fristen" className="text-sm text-[#1A2238] underline decoration-[#D32F2F]" data-testid="all-fristen-link">Alle Fristen</Link>
        </div>
        {stats.upcoming_fristen.length === 0 ? (
          <p className="text-[#4B5563] text-sm">Keine anstehenden Fristen.</p>
        ) : (
          <ul className="divide-y divide-[#E5E7EB]">
            {stats.upcoming_fristen.map(f => (
              <li key={f.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">{f.titel}</p>
                  <p className="text-sm text-[#4B5563]">{f.beschreibung || "–"}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{new Date(f.faellig_am).toLocaleDateString("de-DE")}</p>
                  <p className="overline text-[#D32F2F]">{f.prioritaet}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    offen: ["bg-[#FEF3C7]", "text-[#92400E]", "Offen"],
    in_bearbeitung: ["bg-[#DBEAFE]", "text-[#1E40AF]", "In Bearbeitung"],
    abgeschlossen: ["bg-[#D1FAE5]", "text-[#065F46]", "Abgeschlossen"],
  };
  const [bg, fg, label] = map[status] || ["bg-[#F3F4F6]", "text-[#4B5563]", status];
  return <span className={`inline-flex px-2.5 py-1 rounded text-xs font-medium ${bg} ${fg}`}>{label}</span>;
}
